
import { defineComponent } from 'vue';

import { SocialProfile } from '@/models/Social/social-profile';
import { SocialTab } from '@/models/Social/Profile/social-tab';
import { SocialTabBlocks } from '@/models/Social/Profile/social-tab-blocks';
import { SocialTabProducts } from '@/models/Social/Profile/social-tab-products';
import { SocialTabTemplates } from '@/models/Social/Profile/social-tab-templates';
import { SocialTabQuestions } from '@/models/Social/Profile/social-tab-questions';
import { TemplateStruct } from "@/models/Blocks/Itens/Types/template-struct";
import { HistoryPosition } from '@/models/Blocks/history-position';
import baseUrl from "@/api/base-url";
import {isEmpty} from "lodash-es";
//import request from "@/Utilities/httpRequest";
import shareApi from "@/api/sharedApi";
import utilities from "@/Utilities/common";

export default defineComponent({
    components: {
        ContentSocialBlocks: () => import('@/components/Social/ContentBlocks/ContentSocialBlocks.vue'),
    },
    props: {
        kStrings:{
            type: Object,
            default: () => {
                return {
                    publications: 'Publications',
                    followers: 'Followers',
                    following: 'Following',
                    follow: 'Follow',
                    emptyQuestions: 'No comments yet',
                    newCommentText: 'Ask a question or add a comment',
                    sentMessage: 'Message sent!'
                }
            }
        },
        saveCommentUrl: {
            type: String,
            default: baseUrl.getUrl('socialNetwork/save-comment')
        },
        profile:{
            type: [SocialProfile, Object],
            default: () => new SocialProfile()
        },
        isAppStore:{
            type: Boolean,
            default: false
        },
        isGuest: {
            type: Boolean,
            default: true
        },
        clubMode: {
            type: Boolean,
            default: false
        },
        mention:{
            type: String,
            default: undefined
        },
        isDesktop: {
            type: Boolean,
            default: false
        },
        breadcrumbs: {
            type: [Array, Object],
            default: null
        },
        couponsRaw: {
            type: [Array , null],
            default: null
        },
        contentBlocks: {
            type: Object,
            default: undefined
        },
        beaPartner: {
            type: Object,
            default: {
                url: '',
                openChat: false
            }
        },
        headerProps: {
            type: Object,
            default: undefined
        },
    },
    data(){
        return {
            selectedTab: (new SocialTabBlocks()) as SocialTab,
            gradientLeft: false,
            gradientRight: true,
            showLoader: false,
            //blocksColor: '#28ACB5',
            //fullContent: [] // of tab contents (all open)
        }
    },
    mounted() {
        if ('scrollRestoration' in history) {
            // Back off, browser, I got this...
            history.scrollRestoration = 'manual';
        }
        if(!this.clubMode) {
            this.findSelectTab(this.profile);
        }

        if(HistoryPosition.isBack()){
           this.showLoader = true;
           setTimeout(() => {
            this.showLoader = false;
           }, 100); 
        }

        const url = new URL(window.location.href);
        url.searchParams.delete('#profileContent');
        history.replaceState('', "", " ");
    },
    watch:{
        profile(newProfile: SocialProfile){
            if(!this.clubMode) {
                this.findSelectTab(newProfile);
            }
        }
    },
    computed:{
        fullContent(): Array<SocialTab>|undefined {
            if(this.clubMode) {
                let blockSections = [];
                for (let tab of this.profile.tabs) {
                    if(tab instanceof SocialTabBlocks || tab instanceof SocialTabProducts || tab instanceof SocialTabTemplates) {
                        tab.selected = true; // only loads this way
                        let tabData = tab.getData();
                        let isProductList = tab instanceof SocialTabProducts || tab.contentType == 'products';
                        let section = {
                            tabData: tab,
                            blockData: tabData,
                            isProductList: isProductList,
                            manualLoad: !isProductList ? {
                                hasNextPage: tab.hasNextPage(),
                                hide: !tab.hasNextPage() || isEmpty(tab.getData()),
                                kStrings: {
                                    loadDescription: this.kStrings.kDISCOVER_ALL_MY_PUBLICATIONS,
                                    btnLabel: this.kStrings.kLOAD_MORE,
                                }
                            } : null,
                        }
                        blockSections.push(section);
                    }
                }

                return blockSections;
            }

            return undefined;
        },
        tabSelected(): SocialTab|undefined{
            return !this.clubMode && this.selectedTab instanceof SocialTab ? this.selectedTab : undefined;
        },
        blocks(): Array<TemplateStruct>|undefined{
            if(!this.clubMode && (this.tabSelected instanceof SocialTabBlocks || this.tabSelected instanceof SocialTabProducts || this.tabSelected instanceof SocialTabTemplates)){
                return this.tabSelected.getData();
            }
            return undefined;
        },
        questions(): SocialTabQuestions|undefined{
            return this.tabSelected instanceof SocialTabQuestions ? <SocialTabQuestions> this.tabSelected : undefined;
        },
        followers():string|number{
            return this.profile.followersFormatted ?? this.profile.followers;
        },
        productsMubData():string{
            // no need to check for "tabSelected" instance because getter "contentType" is defined in SocialTab 
            const tabType = this.tabSelected ? this.tabSelected.contentType : 'tab';
            let mub;
            if(this.profile && tabType == 'products'){
                mub = this.profile.productsMub;
            }
            else{
                mub = {
                    b: 'SOCIAL_PROFILE_TAB', 
                    t: tabType == 'social' ? 'socialpost' : tabType,
                }
            }
            return JSON.stringify(mub);
        },
        followText(){
            return this.profile && this.profile.following ? this.kStrings.following : this.kStrings.follow;
        },
    },
    methods: {
        findSelectTab(profile: SocialProfile){
            if(profile.tabs && profile.tabs.length > 0){
                let s = profile.tabs.find( p => p.selected );
                s && (this.selectedTab = s);
            }
        },
        questionsReload(){
            if(this.questions){
                this.questions.reload();
            }
        },
        close(){
            this.showLoader = true;
        },
        selectTab(tab: SocialTab){
            this.profile.tabs.forEach(tab => {
                tab.selected = false;
            });
            tab.selected = true;
            this.selectedTab = tab;     
        },
        scrollEvent(){
            let elem = this.$refs.tabs;
            if(elem instanceof HTMLElement){
                this.gradientLeft = elem.scrollLeft > 1;
                this.gradientRight = elem.scrollWidth - elem.scrollLeft > (elem.offsetWidth + 1);
            }
        },
        async followClick(){
            await this.profile.like.click();
        },
        async shareControl() {
            if(shareApi.checkWebShareApi) {
                navigator.share({
                    title: this.profile.name,
                    url: this.profile.shareOtLink
                });
            } else {
                // todo fail share api
                // link copied?
            }
        },
        goToUrl(url = '') {
            if (url) {
              utilities.goToUrlNewTab(url);
            }
        },
        chatControl() {
            if(!this.profile.ot){
                return;
            }

            let params: {[key: string]: string} = {
                name: btoa(encodeURIComponent(this.profile.name ?? this.profile.nameId)),
                otKey: this.profile.ot
            };
            if(this.isDesktop){
                let baseElem = document.querySelector('#header');
                    if(baseElem){
                    const event = new CustomEvent('actionOpenChat', {
                        detail: params,
                        cancelable: true,
                        bubbles: true
                    });
                    baseElem.dispatchEvent(event);
                }
            }else{
                let url:string|null = baseUrl.getUrl('chat');
                if(url){
                    for (let d in params){
                        let p = encodeURIComponent(d) + '=' + encodeURIComponent(params[d]);
                        url = url + (url.includes('?') ? '&' : '?') + p;
                    }

                    window.location.assign(url);
                }
            }

        }
    }
});
